import { guessCountry, usePrice } from '../../utils/price';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { CarProfile } from '../../cars/CarProfile';
import { useT, getLang } from '../../i18n';
import { addPurchase, getDeviceId, getAccountEmail } from '../../utils/access';
import { drawQR } from '../../utils/qr';
import { invalidateGrant } from '../../utils/grant';
import { isCarBrowser } from '../../utils/platform';
import { AccountModal } from './AccountModal';
import { getSource } from '../../utils/source';
import { iapStore } from '../../utils/iap';

/**
 * PurchaseQR — Stripe one-time purchase. Creates a checkout session, shows the
 * way to pay it, polls until the webhook confirms, then records the purchase and
 * calls onPaid. Used by the Unlock page and the in-drive demo paywall popup.
 *
 * The account comes FIRST (Marc). An engine bought against a signed-in account
 * belongs to the person, not to the screen: it comes back in the car, on the
 * phone and after a reinstall. Paying as a stranger only ever tied it to this
 * device and to whatever address was typed into Stripe.
 *
 * How you pay depends on where you are: a car screen (or any big screen) gets
 * the QR to finish on the phone, a phone or a laptop gets the button that walks
 * straight to Stripe.
 */
export function PurchaseQR({ profile, onPaid, size = 240 }: { profile: CarProfile; onPaid: () => void; size?: number }) {
  const t = useT();
  const price = usePrice();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [state, setState] = useState<'init' | 'ready' | 'unavailable' | 'paid' | 'error'>('init');
  const [payUrl, setPayUrl] = useState('');
  const [email, setEmail] = useState(() => getAccountEmail());
  const [signIn, setSignIn] = useState(false);
  // the QR is for a screen you cannot pay on; everywhere else the button IS the checkout
  const scanIt = isCarBrowser() || (typeof window !== 'undefined' && window.innerWidth >= 900);

  useEffect(() => {
    if (!email) return;   // no checkout is created until there is an account to hang it on
    let stop = false;
    let poll: ReturnType<typeof setInterval> | null = null;
    (async () => {
      try {
        const res = await fetch('/api/checkout', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ carId: profile.id, deviceId: getDeviceId(), email: email ?? undefined, cc: guessCountry(), lang: getLang(), src: getSource() || undefined }),
        });
        if (res.status === 503) return setState('unavailable');
        if (!res.ok) return setState('error');
        const data = (await res.json()) as { url: string; token: string };
        if (stop) return;
        setPayUrl(data.url);
        setState('ready');
        poll = setInterval(async () => {
          try {
            const r = await fetch(`/api/purchase/status?token=${encodeURIComponent(data.token)}`);
            const j = (await r.json()) as { paid: boolean };
            if (j.paid) {
              addPurchase(profile.id);
              invalidateGrant(profile.id); // next engine start asks the server → FULL
              setState('paid');
              if (poll) clearInterval(poll);
              setTimeout(onPaid, 900);
            }
          } catch {
            /* keep polling */
          }
        }, 2500);
      } catch {
        setState('error');
      }
    })();
    return () => {
      stop = true;
      if (poll) clearInterval(poll);
    };
  }, [profile.id, email]);

  useEffect(() => {
    if (state === 'ready' && payUrl && scanIt && canvasRef.current) drawQR(canvasRef.current, payUrl, size);
  }, [state, payUrl, size, scanIt]);

  // the gate: one email, one code, no password — then the till opens
  if (!email) {
    return (
      <div class="pqr pqr--gate">
        {/* minimal on purpose (Marc): the headline and the button. The why is one line under the button
            on the unlock page, and nobody reads a paragraph with a limiter on. */}
        <p class="pqr__gatelead">{t('buyNeedAccount')}</p>
        <button class="acctpanel__btn pqr__gatebtn" onClick={() => setSignIn(true)}>{t('buyNeedAccountBtn')}</button>
        {signIn && <AccountModal onClose={() => { setSignIn(false); setEmail(getAccountEmail()); }} />}
      </div>
    );
  }

  return (
    <div class="pqr">
      {state === 'ready' && (
        <>
          {/* inside the iPhone app there is no web checkout: buying happens in the App Store sheet, and
              pointing at our own shop from in there is exactly what Apple does not allow. The Android app
              ships as an APK, outside Play, so it pays exactly like the site (Stripe, in the browser). */}
          {/ROARDRIVE-App/.test(navigator.userAgent) && iapStore() === 'apple' ? (
            <p class="pqr__scan">{t('storeNotAnswering')}</p>
          ) : scanIt ? (
            <>
              <div class="pqr__qrwrap"><canvas ref={canvasRef} class="pqr__qr" /></div>
              <p class="pqr__scan">{t('pqrScan')}</p>
            </>
          ) : (
            <>
              <a class="acctpanel__btn pqr__pay" href={payUrl}>{t('payNow', { price })}</a>
              <p class="pqr__scan">{t('pqrPayNote', { email })}</p>
            </>
          )}
        </>
      )}
      {state === 'init' && <p class="pqr__scan">{t('pqrPreparing')}</p>}
      {state === 'paid' && <p class="pqr__ok">{t('pqrUnlocked')}</p>}
      {state === 'unavailable' && <p class="pqr__scan">{t('pqrNotConfigured')}</p>}
      {state === 'error' && <p class="pqr__scan">{t('pqrError')}</p>}
    </div>
  );
}
